import ApiService from '../../../../service/ApiService';
import { GS_ENDPOINTS } from './endpoints';
import type { GroundStation } from '../types/mission';
import { ecefToGeodetic, geodeticToEcef } from '../lib/coordinates';

export interface CreateGSPayload {
  groundStationName: string;
  status: string;
  antennaId: string[];
  properties: {
    latitude?: string;
    longitude?: string;
    altitude?: string;
    altitudeReference?: string;
    cartesian?: [number, number, number];
    coordinateUnit: string;
    coordinateType: string;
    minElevationAngle: number;
    FieldOfView: {
      geometry: string;
      conicFOV?: number;
      horizontalFOV?: number;
      verticalFOV?: number;
      pointingAxis: { azimuth: number; elevation: number };
    };
    freqbands: string[];
    provider: string;
  };
}

const toGroundStationRequest = (body: CreateGSPayload): CreateGSPayload => {
  const properties: CreateGSPayload['properties'] = {
    coordinateUnit: body.properties.coordinateUnit,
    coordinateType: body.properties.coordinateType,
    minElevationAngle: body.properties.minElevationAngle,
    FieldOfView: body.properties.FieldOfView,
    freqbands: body.properties.freqbands,
    provider: body.properties.provider,
  };

  if (body.properties.latitude !== undefined) properties.latitude = body.properties.latitude;
  if (body.properties.longitude !== undefined) properties.longitude = body.properties.longitude;
  if (body.properties.altitude !== undefined) properties.altitude = body.properties.altitude;
  if (body.properties.altitudeReference !== undefined) properties.altitudeReference = body.properties.altitudeReference;
  if (body.properties.cartesian !== undefined) properties.cartesian = body.properties.cartesian;

  return {
    groundStationName: body.groundStationName,
    status: body.status,
    antennaId: body.antennaId,
    properties,
  };
};

export const createGroundStation = (missionId: string, scenarioId: string, body: CreateGSPayload) => {
  const endpoint = GS_ENDPOINTS.create(missionId, scenarioId);
  console.log('[groundStationApi] POST CREATE', endpoint);
  return ApiService.post(endpoint, toGroundStationRequest(body));
};

export const getAllGroundStations = (missionId: string, scenarioId: string) =>
  ApiService.get(GS_ENDPOINTS.getAll(missionId, scenarioId));

export const getGroundStationById = (missionId: string, scenarioId: string, gsId: string) =>
  ApiService.get(GS_ENDPOINTS.getById(missionId, scenarioId, gsId));

export const deleteGroundStation = (missionId: string, scenarioId: string, gsId: string) =>
  ApiService.delete(GS_ENDPOINTS.delete(missionId, scenarioId, gsId));

export const updateGroundStation = (
  missionId: string,
  scenarioId: string,
  gsId: string,
  body: CreateGSPayload,
) => {
  const endpoint = GS_ENDPOINTS.update(missionId, scenarioId, gsId);
  console.log('[groundStationApi] PATCH UPDATE', endpoint);
  return ApiService.patch(endpoint, toGroundStationRequest(body));
};

export const renameGroundStation = async (missionId: string, scenarioId: string, gsId: string, name: string) => {
  const res = await getGroundStationById(missionId, scenarioId, gsId);
  const current = res?.data?.data ?? res?.data ?? {};
  return updateGroundStation(missionId, scenarioId, gsId, {
    groundStationName: name,
    status: current.status ?? 'Configure',
    antennaId: current.antennaId ?? [''],
    properties: current.properties ?? {},
  });
};

export const deleteGroundStations = (missionId: string, scenarioId: string, groundStationIds: string[]) =>
  ApiService.post(GS_ENDPOINTS.deleteMany(missionId, scenarioId), { groundStationIds });

// Maps a raw API response object to the internal GroundStation type for Cesium
export function mapApiGSToInternal(gs: any): GroundStation {
  const fovGeometry = gs.properties?.FieldOfView?.geometry?.toLowerCase();

  let lat = parseFloat(gs.properties?.latitude);
  let lon = parseFloat(gs.properties?.longitude);
  let altKm = parseFloat(gs.properties?.altitude);

  let x = gs.properties?.cartesian?.[0];
  let y = gs.properties?.cartesian?.[1];
  let z = gs.properties?.cartesian?.[2];

  const hasCartesian = x != null && y != null && z != null && (x !== 0 || y !== 0 || z !== 0);

  if ((isNaN(lat) || isNaN(lon) || (lat === 0 && lon === 0)) && hasCartesian) {
    const geo = ecefToGeodetic(x, y, z);
    lat = geo.lat;
    lon = geo.lon;
    if (isNaN(altKm) || altKm === 0) altKm = geo.altKm;
  } else if (!hasCartesian && !isNaN(lat) && !isNaN(lon)) {
    const ecef = geodeticToEcef(lat, lon, isNaN(altKm) ? 0 : altKm);
    x = ecef.x;
    y = ecef.y;
    z = ecef.z;
  }

  return {
    id:           gs.groundStationId,
    name:         gs.groundStationName,
    active:       true,
    lat:          isNaN(lat) ? 0 : lat,
    lon:          isNaN(lon) ? 0 : lon,
    altKm:        isNaN(altKm) ? 0 : altKm,
    minElevation: gs.properties?.minElevationAngle ?? 5,
    coordType:    gs.properties?.coordinateType?.toLowerCase() as any,
    altDatum:     gs.properties?.altitudeReference as any,
    fovGeometry:  (fovGeometry === 'conical' ? 'conic' : fovGeometry) as any,
    fovDeg:       gs.properties?.FieldOfView?.conicFOV,
    fovH:         gs.properties?.FieldOfView?.horizontalFOV,
    fovV:         gs.properties?.FieldOfView?.verticalFOV,
    pointingAz:   gs.properties?.FieldOfView?.pointingAxis?.azimuth,
    pointingEle:  gs.properties?.FieldOfView?.pointingAxis?.elevation,
    x,
    y,
    z,
    provider:     gs.properties?.provider,
    freqbands:    gs.properties?.freqbands,
  };
}

